<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import RadioRow from '@/components/RadioRow.vue'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'
import ResourceCard from '@/resources/ResourceCard.vue'
import PresentLevelsStandardView from '@/standards/PresentLevelsStandardView.vue'
import { headingsForStandardSetCode } from '@/standards/standard_headings'
import { levelGroupLabelFor } from '@/presentlevels/no-ai/level_group_label'
import { levelLayoutFor } from '@/browse/presentlevels/present_levels_browse'
import type { SubjectType } from '@/browse/presentlevels/present_levels_browse'
import { resourcesByUuid, standardsBySetCode, statementTextAtLevel } from '@/browse/presentlevels/statement_content'
import type { BrowseStatement } from '@/browse/presentlevels/statement_content'
import type { Skill } from '@/presentlevels/no-ai/skill_statement_types'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'

const props = defineProps<{
  category: Skill
  statements: BrowseStatement[]
  selectedLevel: string
  selectedGrade: string
  subjectType: SubjectType | undefined
}>()

const emit = defineEmits<{ selectLevel: [level: string] }>()

// Vue 2's resource tiles here reported a null list position on every resource event.
const RESOURCE_EVENT_CONTEXT = { position: null }

const analytics = getAnalyticsTracker()
const { profileId, visibleStandardSets } = storeToRefs(useContentProfileStore())

// Held by the page, since a grade change remounts this list while the level is re-chosen.
const editedTextByLevel = defineModel<Record<string, string>>('editedTextByLevel', { default: () => ({}) })
const isEditingStatement = ref(false)

const currentStatement = computed(() => props.statements.find((statement) => statement.level_label === props.selectedLevel))
const defaultStatementText = computed(() => {
  if (!currentStatement.value) {
    return ''
  }
  return statementTextAtLevel(currentStatement.value, props.selectedLevel)
})
const currentStatementText = computed(() => editedTextByLevel.value[props.selectedLevel] || defaultStatementText.value)
const statementLabel = computed(() => (currentStatement.value ? `${currentStatement.value.skill_name} statement` : undefined))
const currentResources = computed(() => (currentStatement.value ? resourcesByUuid(currentStatement.value) : {}))
const hasResources = computed(() => Object.keys(currentResources.value).length > 0)
const currentStandardsBySetCode = computed(() => (currentStatement.value ? standardsBySetCode(currentStatement.value, visibleStandardSets.value) : {}))
const hasStandards = computed(() => Object.keys(currentStandardsBySetCode.value).length > 0)

const levelLayout = computed(() => levelLayoutFor(props.subjectType))
const levelOptions = computed(() => props.statements.map((statement) => ({ label: statement.level_label, locked: statement.locked })))
const levelGroupLabel = computed(() => levelGroupLabelFor(props.category.skill_name))
// A new grade or category, or a level the page chose itself, remounts the row so it shows that level.
// A level picked in the row keeps the row, or the remount would drop keyboard focus to the page.
let levelPickedInRow = ''
const levelChangesFromPage = ref(0)
const levelRowKey = computed(() => `${props.selectedGrade}${props.category.skill_uuid}${levelChangesFromPage.value}`)

watch(() => props.selectedLevel, (level) => {
  if (level !== levelPickedInRow) {
    levelChangesFromPage.value++
  }
  levelPickedInRow = ''
})

function selectLevel([level]: string[]) {
  levelPickedInRow = level
  emit('selectLevel', level)
}

// An empty edit falls back to the statement as written.
function keepEdit(text: string) {
  if (!text) {
    return
  }
  editedTextByLevel.value = { ...editedTextByLevel.value, [props.selectedLevel]: text }
}

// The editor sends a reset outside edit mode, and only an edit is reported.
function updateStatementText(text: string) {
  const isEdit = isEditingStatement.value && text !== currentStatementText.value
  if (isEdit) {
    analytics.sendEvent(EVENT_NAMES.BROWSE_PRESENT_LEVELS.EDITED_STATEMENT, { edited_text: text })
  }
  keepEdit(text)
}

function standardHeading(setCode: string): string {
  return headingsForStandardSetCode(setCode, { profileId: profileId.value })
}
</script>

<template>
  <div>
    <div>
      <h2 class="mb-2.5 text-display-md font-medium leading-[1.1] text-gray-900">{{ category.skill_name }}</h2>
      <RadioRow
        :key="levelRowKey"
        class="mb-4 text-md font-bold text-gray-900"
        :columns="levelLayout.columns"
        :label-position="levelLayout.labelPosition"
        :option-set="levelOptions"
        :default-options="[selectedLevel]"
        :group-label="levelGroupLabel"
        @change="selectLevel"
      />
    </div>
    <div class="rounded-md border border-gray-1000 p-8">
      <CopyTrackWrapper
        class="[&_p]:max-w-none [&_p]:!mb-0"
        type="presentlevel"
        :title="currentStatement?.skill_name"
        :uuid="currentStatement?.skill_uuid"
        :level="selectedLevel"
        gate-copy
      >
        <WikiTextEditor
          format="markdown"
          outer-class="text-text-2xl font-regular leading-8"
          :wiki-text="currentStatementText"
          :reset-value="defaultStatementText"
          show-reset
          :resources="currentResources"
          :label="statementLabel"
          @update:wiki-text="updateStatementText"
          @edit-mode-updated="isEditingStatement = $event"
        />
      </CopyTrackWrapper>

      <section v-if="hasResources" class="mt-8">
        <h3 class="mb-2.5 mt-5 text-display-sm font-medium leading-[1.1] text-purple-300">Assessment Resources</h3>
        <div class="flex flex-wrap">
          <ResourceCard
            v-for="resource in currentResources"
            :key="resource.uuid"
            v-bind="resource"
            :heading-level="4"
            :event-context="RESOURCE_EVENT_CONTEXT"
          />
        </div>
      </section>

      <section v-if="hasStandards" class="mt-8">
        <div v-for="(standards, setCode) in currentStandardsBySetCode" :key="setCode">
          <h3 class="mb-2.5 mt-5 text-display-sm font-medium leading-[1.1] text-purple-300">{{ standardHeading(setCode) }}</h3>
          <CopyTrackWrapper
            v-for="standard in standards"
            :key="standard.standard_long_ref"
            class="text-gray-700"
            type="pl-standard"
            :title="standard.standard_long_ref"
            :uuid="standard.standard_long_ref"
            gate-copy
          >
            <PresentLevelsStandardView
              :set-code="standard.standard_set_code"
              :short-ref="standard.standard_short_ref"
              :standard-text="standard.standard_text"
              :standard-info="standard"
            />
          </CopyTrackWrapper>
        </div>
      </section>
    </div>
  </div>
</template>
